/*
  Estimated delivery date widget (PDP).
  Looks a pincode up in the lazily fetched edd-pincode-data.js bucket map and
  prints the resulting delivery date range. Dates are always computed in IST so
  a visitor with a skewed or foreign device clock still sees correct dates.
*/
class ProductEdd extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('.product-edd__form');
    this.input = this.querySelector('.product-edd__input');
    this.button = this.querySelector('.product-edd__button');
    this.result = this.querySelector('.product-edd__result');
    this.dataUrl = this.dataset.dataUrl;
    this.successPrefix = this.dataset.successPrefix;
    this.errorText = this.dataset.errorText;
    this.invalidText = this.dataset.invalidText;
    this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.input.addEventListener('input', this.onInput.bind(this));
  }

  onInput() {
    this.input.value = this.input.value.replace(/\D/g, '').slice(0, 6);
    this.clearResult();
  }

  async onSubmit(event) {
    event.preventDefault();
    const pincode = this.input.value.trim();

    if (!/^\d{6}$/.test(pincode)) {
      this.showResult(this.invalidText, false);
      return;
    }

    this.button.setAttribute('aria-busy', 'true');
    this.button.disabled = true;

    try {
      const minDays = await this.lookup(pincode);
      if (minDays === null) {
        this.showResult(this.errorText, false);
      } else {
        // Client asked for every date one day earlier than the pincode data gives
        this.showResult(`${this.successPrefix} ${this.formatRange(minDays - 1, minDays)}`, true);
      }
    } catch (error) {
      console.error('EDD lookup failed', error);
      this.showResult(this.errorText, false);
    } finally {
      this.button.removeAttribute('aria-busy');
      this.button.disabled = false;
    }
  }

  async lookup(pincode) {
    await this.loadData();
    const pincodes = window.MOXIE_EDD_PINCODES;

    for (const minDays in pincodes) {
      if (ProductEdd.bucketHas(pincodes[minDays], pincode)) return Number(minDays);
    }

    return null;
  }

  // Each bucket is sorted 6-digit records, so binary search on record
  // boundaries. Scanning with indexOf would not work: a pincode can appear
  // unaligned across two neighbouring records and mask the real entry.
  static bucketHas(bucket, pincode) {
    let low = 0;
    let high = bucket.length / 6 - 1;

    while (low <= high) {
      const mid = (low + high) >> 1;
      const candidate = bucket.substr(mid * 6, 6);
      if (candidate === pincode) return true;
      if (candidate < pincode) low = mid + 1;
      else high = mid - 1;
    }

    return false;
  }

  loadData() {
    if (window.MOXIE_EDD_PINCODES) return Promise.resolve();

    if (!ProductEdd.dataRequest) {
      ProductEdd.dataRequest = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = this.dataUrl;
        script.addEventListener('load', resolve);
        script.addEventListener('error', () => {
          ProductEdd.dataRequest = null;
          reject(new Error('Unable to load EDD pincode data'));
        });
        document.head.appendChild(script);
      });
    }

    return ProductEdd.dataRequest;
  }

  formatRange(minDays, maxDays) {
    const today = ProductEdd.todayInIst();
    return `${ProductEdd.formatDate(ProductEdd.addDays(today, minDays))} - ${ProductEdd.formatDate(
      ProductEdd.addDays(today, maxDays)
    )}`;
  }

  showResult(message, isSuccess) {
    this.result.textContent = message;
    this.result.classList.toggle('product-edd__result--success', isSuccess);
    this.result.classList.toggle('product-edd__result--error', !isSuccess);
    this.result.hidden = false;
  }

  clearResult() {
    this.result.hidden = true;
    this.result.textContent = '';
  }

  // Midnight IST today, expressed as a UTC date so day arithmetic stays free of
  // local timezone and DST drift.
  static todayInIst() {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
    const [year, month, day] = parts.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day));
  }

  static addDays(date, days) {
    return new Date(date.getTime() + days * 86400000);
  }

  // Formatted by hand rather than via Intl: short weekday and month names vary
  // between browser ICU versions ("Sep" vs "Sept"), and the widget should read
  // identically everywhere.
  static formatDate(date) {
    const weekday = ProductEdd.WEEKDAYS[date.getUTCDay()];
    const month = ProductEdd.MONTHS[date.getUTCMonth()];
    return `${weekday}, ${date.getUTCDate()} ${month}`;
  }
}

ProductEdd.dataRequest = null;
ProductEdd.WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
ProductEdd.MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

if (!customElements.get('product-edd')) {
  customElements.define('product-edd', ProductEdd);
}
